import { useEffect, useState } from 'react';
import { View } from 'react-native';
import { useTranslation } from 'react-i18next';
import type { ConnectedAppCvSummary, UpdateConnectedAppInput, CvDocumentSelection } from '@tabbio/types';
import { spacing } from '@tabbio/tokens';
import { CvEditionSelector } from './CvEditionSelector';
import { CvSelectedEditionPreview } from './CvSelectedEditionPreview';
import { NativeButton } from '../common/NativeButton';
import { Text } from '../common/AppText';
import { useExactCvEdition } from '../../hooks/useExactCvEdition';
import { useTheme } from '../../hooks/useTheme';
import { cvReviewEnabled } from '../../lib/cv-release-api';

export function ConnectedCvEditionControl({ target, current, pending, onSave }: {
  target: string; current: ConnectedAppCvSummary | null; pending: boolean; onSave(input: UpdateConnectedAppInput): void;
}) {
  const { t } = useTranslation('seeker');
  const { theme } = useTheme();
  const [variantId, setVariantId] = useState<string | null>(current?.selection?.variantId ?? null);
  const [pinned, setPinned] = useState<CvDocumentSelection | null>(current?.selection ?? null);
  useEffect(() => { setVariantId(current?.selection?.variantId ?? null); setPinned(current?.selection ?? null); }, [target, current?.selection]);
  const selected = useExactCvEdition(target, variantId, true, pinned);
  if (!cvReviewEnabled) return null;
  return <View style={{ gap: spacing[2], paddingVertical: spacing[3] }}>
    {current?.edition ? <Text style={{ color: theme.textSecondary }}>{t('cv.review.sharedEdition', { locale: t(`cv.review.locale.${current.edition.locale}`, { defaultValue: current.edition.locale }) })}</Text> : null}
    <CvEditionSelector target={target} value={variantId} disabled={pending} onChange={(value) => {
      setVariantId(value);
      setPinned(null);
      if (!value) onSave({ cvId: target, cvSelection: null });
    }} />
    {variantId || pinned ? <>
      <CvSelectedEditionPreview document={selected.data} pending={selected.isPending} unavailable={!selected.isPending && !selected.ready} />
      <NativeButton title={t('cv.review.saveSharedEdition')} variant="outline" disabled={pending || !selected.ready}
        onPress={() => { if (selected.selection) onSave({ cvId: target, cvSelection: selected.selection }); }} />
    </> : null}
  </View>;
}
